// The "save each drawn polygon in turn" queue behind the save dialog, as a pure reducer.
// Ids are the editor's polygon ids; the page decides what saving one means.

export interface SaveQueue {
  ids: string[];
  index: number;
  open: boolean;
  /** True once the first save while editing has been made: only that one updates the saved area. */
  editApplied: boolean;
  /** Ids the user skipped in this batch: out of the queue, never saved, left on the map. */
  skipped: string[];
}

export const emptyQueue: SaveQueue = { ids: [], index: 0, open: false, editApplied: false, skipped: [] };

export type QueueAction =
  | { type: 'open'; ids: string[] }
  /** `update: false` says the save made a new area, so it does not use up the edit. */
  | { type: 'saved'; update?: boolean }
  | { type: 'skip' }
  | { type: 'cancel' };

export interface QueueResult {
  state: SaveQueue;
  /** open: there was nothing to save. */
  empty?: boolean;
  /** saved or skip: that was the last one. */
  done?: boolean;
}

export function reduceQueue(state: SaveQueue, action: QueueAction): QueueResult {
  switch (action.type) {
    case 'open':
      if (action.ids.length === 0) return { state: { ...state, ids: [], index: 0, open: false, skipped: [] }, empty: true };
      return { state: { ...state, ids: [...action.ids], index: 0, open: true, skipped: [] } };
    case 'saved': {
      const ids = state.ids.filter((_, i) => i !== state.index);
      const editApplied = state.editApplied || action.update !== false;
      if (ids.length === 0) return { state: { ...state, ids, index: 0, open: false, editApplied }, done: true };
      return { state: { ...state, ids, index: state.index >= ids.length ? 0 : state.index, editApplied } };
    }
    case 'skip': {
      // A skipped polygon leaves the queue at once, so it is never saved or shown again.
      if (state.ids.length === 0) return { state };
      const skipped = [...state.skipped, state.ids[state.index]];
      const ids = state.ids.filter((_, i) => i !== state.index);
      if (ids.length === 0) return { state: { ...state, ids, index: 0, open: false, skipped }, done: true };
      return { state: { ...state, ids, index: state.index >= ids.length ? 0 : state.index, skipped } };
    }
    case 'cancel':
      return { state: { ...state, ids: [], index: 0, open: false } };
  }
}

export function currentId(state: SaveQueue): string | null {
  return state.ids[state.index] ?? null;
}

export function progress(state: SaveQueue): { current: number; total: number } {
  return { current: state.ids.length === 0 ? 0 : state.index + 1, total: state.ids.length };
}
